import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PendingIcon from '@mui/icons-material/Pending';
import SecurityIcon from '@mui/icons-material/Security';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ComputerIcon from '@mui/icons-material/Computer';
import { getDashboardData, getQRStats, QRStats } from '../../services/api/dashboardApi';
import { DashboardData } from '../../types/common';
import LineChart from '../../components/charts/LineChart';
import PieChart from '../../components/charts/PieChart';
import BarChart from '../../components/charts/BarChart';

const DashboardPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [qrStats, setQRStats] = useState<QRStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const QR_REDIRECT_URL = 'https://api.drphishing.com/r/playstore';
  const QR_IMAGE_URL = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(QR_REDIRECT_URL)}`;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, qr] = await Promise.all([
        getDashboardData(),
        getQRStats().catch((err) => {
          console.error('QR Stats fetch error:', err);
          return null;
        }),
      ]);
      setDashboardData(data);
      setQRStats(qr);
      console.log('QR Stats loaded:', qr);
    } catch (err: any) {
      setError(err.message || '대시보드 데이터를 불러오는데 실패했습니다.');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4">{value.toLocaleString()}</Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      case 'SAFE':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'approved':
        return '승인됨';
      case 'rejected':
        return '거부됨';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { stats, userTrend, recentAnalyses, recentReports, riskDistribution } = dashboardData;

  // Prepare data for pie chart
  const riskDistributionData = Object.entries(riskDistribution).map(([name, value]) => ({
    name,
    value,
  }));

  const riskColors = ['#f44336', '#ff9800', '#2196f3', '#4caf50'];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        대시보드
      </Typography>

      {/* Statistics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title="총 사용자"
          value={stats.totalUsers}
          icon={<PeopleIcon sx={{ color: 'white' }} />}
          color="#1976d2"
        />
        <StatCard
          title="오늘 가입"
          value={stats.todayUsers}
          icon={<PersonAddIcon sx={{ color: 'white' }} />}
          color="#2e7d32"
        />
        <StatCard
          title="대기 중인 신고"
          value={stats.pendingReports}
          icon={<PendingIcon sx={{ color: 'white' }} />}
          color="#ed6c02"
        />
        <StatCard
          title="총 분석 건수"
          value={stats.totalAnalyses}
          icon={<SecurityIcon sx={{ color: 'white' }} />}
          color="#9c27b0"
        />
      </Box>

      {/* Charts Section */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        {/* User Registration Trend */}
        <LineChart
          data={userTrend}
          xKey="date"
          yKey="count"
          title="사용자 가입 추이 (최근 7일)"
          height={300}
          color="#1976d2"
        />

        {/* Risk Distribution */}
        {riskDistributionData.length > 0 && (
          <PieChart
            data={riskDistributionData}
            nameKey="name"
            valueKey="value"
            title="위험도별 APK 분석 분포"
            height={300}
            colors={riskColors}
          />
        )}
      </Box>

      {/* Recent Data Tables */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
          },
          gap: 3,
        }}
      >
        {/* Recent APK Analyses */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            최근 APK 분석 결과
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>파일명</TableCell>
                  <TableCell>위험도</TableCell>
                  <TableCell>날짜</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentAnalyses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      데이터가 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  recentAnalyses.map((analysis: any) => (
                    <TableRow key={analysis._id}>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {analysis.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={analysis.riskEvaluation?.riskLevel || 'N/A'}
                          color={getRiskLevelColor(analysis.riskEvaluation?.riskLevel)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(analysis.createdAt).toLocaleDateString('ko-KR')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Recent Phone Reports */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            최근 전화번호 신고
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>전화번호</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>날짜</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      데이터가 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  recentReports.map((report: any) => (
                    <TableRow key={report.id}>
                      <TableCell>{report.phoneNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(report.status)}
                          color={getStatusColor(report.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(report.createdAt).toLocaleDateString('ko-KR')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* QR Code Statistics Section */}
      {qrStats && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Play Store QR코드 통계
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {/* QR Code Card */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                QR코드
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <img src={QR_IMAGE_URL} alt="Play Store QR Code" style={{ width: 150, height: 150 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                    {QR_REDIRECT_URL}
                  </Typography>
                  <Tooltip title="URL 복사">
                    <IconButton size="small" onClick={() => copyToClipboard(QR_REDIRECT_URL)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <StatCard
                  title="총 클릭 수"
                  value={qrStats.playstore.total}
                  icon={<QrCodeIcon sx={{ color: 'white' }} />}
                  color="#2A7789"
                />
              </Box>
            </Paper>

            {/* Click Trend Chart */}
            <BarChart
              data={qrStats.playstore.allDays}
              xKey="date"
              yKey="count"
              title="QR 클릭 추이 (전체)"
              height={280}
              color="#2A7789"
            />

            {/* Device & Referrer Stats */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                디바이스별 클릭
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneAndroidIcon color="primary" />
                    <Typography>모바일</Typography>
                  </Box>
                  <Typography variant="h6">{qrStats.playstore.devices.mobile}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ComputerIcon color="secondary" />
                    <Typography>데스크톱</Typography>
                  </Box>
                  <Typography variant="h6">{qrStats.playstore.devices.desktop}</Typography>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                유입 경로 TOP 5
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {qrStats.playstore.topReferrers.slice(0, 5).map((ref, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{ref.domain}</TableCell>
                        <TableCell align="right">{ref.count}</TableCell>
                      </TableRow>
                    ))}
                    {qrStats.playstore.topReferrers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          데이터 없음
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DashboardPage;
